import { useApplicationStore } from "@/stores/Application";
import { useChannelsStore } from "@/stores/Channels";
import { isRiverTV, isSamsung, isVMedia, AppError, AppErrorCode } from "@ca.vmedia.tv/domains";
import { linkItems } from "@/components/Header/types/LinkItemsCollection";
import { useRedirectToLogin } from "@/composables/useRedirectToLogin";
import  { type LoginSuccessMutationParams, LoginRequestMutationParams } from "@/stores/Account/types";
import { useLanguagesUtils } from "@/composables/useLanguagesUtils";
import { useAccountStore } from "@/stores/Account";
import { useUserSettingsStore } from "@/stores/UserSettings";
import { useFavoriteChannelsStore } from "@/stores/FavoriteChannels";
import { useRoute } from 'vue-router';
import { useClientMessagesStore } from "@/stores/ClientMessages";
import { Logger, UrlPathUtils } from '@/utils';
import { useGuideStore } from "@/stores/Guide";
import { ScreenName } from "@/common/types/screen-names";
import { useStorageStreamProgress } from "@/composables/player/useStorageStreamProgress/useStorageStreamProgress";

export default defineNuxtPlugin(async () => {
    const GuideStoreModule = useGuideStore();
    const {
        initGuideFilter
    } = GuideStoreModule;

    const { getImageUrlTemplates } = useApplicationStore();

    const { startReceiving } = useClientMessagesStore();

    const { getChannels } = useChannelsStore();

    const {
        LoginWithMacAddress,
        SilentLoginToken,
        SilentLoginOkta,
        LoginSuccess,
        SaveLogin,
    } = useAccountStore();
    const {
        fetchSettings,
    } = useUserSettingsStore();

    const {
        fetchFavChannelsIds,
    } = useFavoriteChannelsStore();

    const { $domains, $platform, $router, $sentry, $analytics } = useNuxtApp();
    const currentRoute = useRoute();
    const {featureOktaLoginEnabled, authMethod, mainPage} = useAppConfig();
    const featureMacAddressLoginEnabled = authMethod.featureMacAddressLoginEnabled;
    const featureRefreshTokenLoginEnabled = authMethod.featureRefreshTokenLoginEnabled;
    const { initLanguage, adjustLanguageSettings } = await useLanguagesUtils();
    
    const { sendStreamProgressToApi } = useStorageStreamProgress();
    sendStreamProgressToApi();

    $domains.lsCacher.deleteExpired();
    $domains.idbCacher.deleteExpired().catch((e) => {
        console.error(e);
    });

    $domains.setIsRiverTV(isRiverTV);
    $domains.setIsVmediaTV(isVMedia);

    let errorMessage: string | null = null;
    let errorCode: AppErrorCode | null = AppErrorCode.OK;

    initLanguage()
    // Register key for tizen
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (isSamsung && (window as any)?.tizen) {
        // Docs: https://developer.samsung.com/smarttv/develop/api-references/tizen-web-device-api-references/tvinputdevice-api.html#TVInputDevice-TVInputDeviceManager-registerKeyBatch
        const keys = ['MediaPause', 'MediaPlay', 'MediaRewind', 'MediaFastForward', 'MediaStop', 'Minus', 'MediaTrackPrevious', 'MediaTrackNext', 'ChannelUp', 'ChannelDown', 'PreviousChannel'];
        for (let i = 0; i <= 9; i++) {
            keys.push(`${i}`);
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any)?.tizen.tvinputdevice.registerKeyBatch(keys, null, (err: ErrorCallback) => {
            Logger.captureLog('The following error occurred: ' +  err.name)
        });
    }
    

    if (!$platform.isRunAllowed()) {
        $router.beforeEach((to, _from, next) => {
            if (to.path !== linkItems.error.routerPath) { 
                errorCode = AppErrorCode.NOT_SUPPORTED_PLATFORM;
                next(linkItems.error.routerPath + `?errorCode=${errorCode}`); 
            } else {
                next();
            }
        });
        return;
    }

    await getImageUrlTemplates();
    
    let loginSuccess = false;
    const userInfo = await $domains.loadUserInfo();

    if (featureMacAddressLoginEnabled && userInfo.sessionId === null) {
        await LoginWithMacAddress().then((res)=>{
            errorCode = res;
        }).catch((err) => {
            if (err instanceof AppError) {
                errorCode = err?.code;
                errorMessage = err?.message;
            } else {
                errorCode = AppErrorCode.LOGIN_NETWORK_ERROR;
                errorMessage = (err as Error)?.message;
            }
        });
    }

    // try to load user info from the local storage
    const { username, sessionId } = await $domains.loadUserInfo();

    if (sessionId !== null) {
        try {
            // auto-login
            if (featureRefreshTokenLoginEnabled) {
                await SilentLoginToken().then((res)=>{
                    errorCode = res;
                });
            } else if (featureOktaLoginEnabled) {
                await SilentLoginOkta().then((res)=>{
                    errorCode = res;
                });
            } else {
                LoginSuccess({ sessionId: sessionId, UserSystemID: username } as LoginSuccessMutationParams);
                SaveLogin(new LoginRequestMutationParams(username ?? ''));
            }
            // auto-login success, exception otherwise
            // load settings
            await fetchSettings();

            await adjustLanguageSettings();
            await fetchFavChannelsIds(); //TODO: Maybe we can remove this. We use it in other places
            // todo: load messages
            // go to home page
            //
            loginSuccess = errorCode === AppErrorCode.OK ? true : false;
            startReceiving().catch((e) => {
                console.error('[startup] startReceiving failed:', e);
            });
        } catch (err) {
            if (err instanceof AppError) {
                errorCode = err?.code;
                errorMessage = err?.message;
            } else {
                errorCode = AppErrorCode.LOGIN_NETWORK_ERROR;
                errorMessage = (err as Error)?.message;
            }
        }
    } else {
        $sentry.setUserId(null);
    }
   
    // TODO: no need to load all channels at once on every page
    try {
        await getChannels();
    } catch (e) {
        console.error('[startup] getChannels failed:', e);
    }

    initGuideFilter();
    
    if ($analytics) {
        $analytics.createOpenAppEvent(ScreenName.SPLASH_SCREEN, ScreenName.SPLASH_SCREEN);
    }

    if (currentRoute.path === linkItems.root.routerPath) {
        setTimeout(() => {
            if (errorCode == AppErrorCode.OK) {
                if (loginSuccess) {
                    void $router.push(linkItems[mainPage].routerPath);
                } else {
                    useRedirectToLogin();
                }
            } else {
                let url = UrlPathUtils.buildUrl(linkItems.error.routerPath, {errorCode, errorMessage});
                switch (errorCode) {
                    case AppErrorCode.LOGIN_TOKEN_ERROR:
                            url = linkItems.login.routerPath;
                        break;
                }
                void $router.push(url);
            }
        }, 100);
    }
});
